import { NonTimezoneSensitiveDate } from './non-time-zone-sensitive-date';

export interface FYOption {
  label: string;
  period: { startDate: string; endDate: string };
  value: string;
}

/**
 * Build fiscal-year options using `NonTimezoneSensitiveDate`
 *
 * @param fyEndDay    1-31  → the day on which the FY closes
 * @param fyEndMonth  1-12  → the month on which the FY closes
 * @param yearsBack   how many *closed* FYs to include besides the current/open one (default = 5)
 * @param today       inject a custom "now" for tests (defaults to `new Date()`)
 *
 * @returns newest-to-oldest array of `FYOption`
 */
export function buildFYOptions(
  fyEndDay: number,
  fyEndMonth: number,
  yearsBack = 5,
  today: Date = new Date()
): FYOption[] {
  if (!fyEndDay || !fyEndMonth) return [];

  // Basic range validation
  if (fyEndMonth < 1 || fyEndMonth > 12 || fyEndDay < 1 || fyEndDay > 31) {
    throw new RangeError('fyEndDay or fyEndMonth out of range');
  }

  // Validate that the day exists in the given month
  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (fyEndDay > daysInMonth[fyEndMonth - 1]) {
    throw new RangeError(
      `Day ${fyEndDay} does not exist in month ${fyEndMonth}`
    );
  }

  const todayNTS = NonTimezoneSensitiveDate.fromNativeDate(today);

  // Helper function to safely create fiscal year end date, handling Feb 29 in non-leap years
  const createFYEndDate = (year: number): NonTimezoneSensitiveDate => {
    if (fyEndMonth === 2 && fyEndDay === 29) {
      // Check if it's a leap year
      const isLeapYear =
        (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
      if (!isLeapYear) {
        // Use Feb 28 for non-leap years
        return new NonTimezoneSensitiveDate(year, 2, 28);
      }
    }
    return new NonTimezoneSensitiveDate(year, fyEndMonth, fyEndDay);
  };

  // Determine which fiscal year we're currently in
  const fyEndThisCalendarYear = createFYEndDate(todayNTS.getYear());

  // If today is after this calendar year's FY end, we're in the next FY
  const currentFYEndYear = todayNTS.gt(fyEndThisCalendarYear)
    ? todayNTS.getYear() + 1
    : todayNTS.getYear();

  const options: FYOption[] = [];

  for (let i = 0; i <= yearsBack; i++) {
    const endYear = currentFYEndYear - i;

    // 1. FY closing date
    const fyEnd = createFYEndDate(endYear);

    // 2. FY start = (FY-end of previous year) + 1 day
    const prevFYEnd = createFYEndDate(endYear - 1);
    const fyStart = prevFYEnd.add(1, 'days');

    // 3. Fiscal year label should be based on the START year
    // This aligns with getFiscalYearOfDate comment: "Fiscal year 2023 typically means the fiscal year starting in 2023"
    const fyStartYear = fyStart.getYear();

    options.push({
      label: `FY ${fyStartYear}`,
      period: {
        startDate: fyStart.toISO8601String(),
        endDate: fyEnd.toISO8601String(),
      },
      // Use the start year as the value to be consistent with the label
      value: fyStartYear.toString(),
    });
  }

  return options;
}
